import type { AIProvider, GenerateRequest, ProviderInfo, StreamHandler, UsageStats } from '../types'
import { AiError } from '../types'
import { readJsonOrThrow } from '../streaming'
import { fetchOrThrow } from '../network'
import type { ProviderConfig } from '@/types/settings'
import { TEST_PROMPT } from '../prompts'
import { track } from '@/composables/useEventTracker'

/**
 * Adapter para Ollama (modelos locales).
 *
 * Diferencias clave frente a OpenAI/Anthropic:
 *   - Sin API key (corre en local, por defecto http://localhost:11434).
 *   - Endpoint `/api/chat` con mensajes tipo OpenAI.
 *   - Streaming: NDJSON (una línea JSON por chunk, sin prefijo `data:`),
 *     cada objeto tiene `message.content`.
 *   - Uso: `prompt_eval_count` / `eval_count` en el objeto final.
 */
export class OllamaProvider implements AIProvider {
  readonly info: ProviderInfo

  constructor(private cfg: ProviderConfig) {
    this.info = { provider: 'ollama', model: cfg.model }
  }

  private get endpoint(): string {
    return `${this.cfg.baseUrl.replace(/\/$/, '')}/api/chat`
  }

  private buildBody(req: GenerateRequest, stream: boolean) {
    // Chat multi-turno: historial directo. Si no, system + user.
    // Ollama NO soporta function-calling (rol `tool` ni `tool_calls`): si el
    // historial los trae (p. ej. reutilizado por un agente), los filtramos
    // para que no fallen las llamadas. El agente cae al RAG si el usuario
    // tiene Ollama como proveedor activo.
    const messages: { role: 'system' | 'user' | 'assistant'; content: string }[] =
      req.messages
        ? req.messages
            .filter((m) => m.role !== 'tool' && !(m.role === 'assistant' && m.toolCalls))
            .map((m) => ({ role: m.role as 'system' | 'user' | 'assistant', content: m.content }))
        : (() => {
            const m: { role: 'system' | 'user'; content: string }[] = []
            if (req.system) m.push({ role: 'system', content: req.system })
            m.push({ role: 'user', content: req.user })
            return m
          })()

    return {
      model: this.cfg.model,
      messages,
      options: {
        temperature: req.temperature ?? 0.4,
        num_predict: req.maxTokens ?? 1024,
      },
      stream,
    }
  }

  async stream(
    req: GenerateRequest,
    onToken: StreamHandler,
  ): Promise<{ text: string; usage: UsageStats }> {
    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.buildBody(req, true)),
      signal: req.signal,
    }, 'Ollama')

    if (!response.ok) {
      await readJsonOrThrow(response)
      throw new AiError('Respuesta inválida de Ollama.', response.status)
    }

    const result = await readOllamaStream(response, req.signal, onToken)
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'ollama',
      model: this.cfg.model,
      tokens: (result.usage.tokensIn ?? 0) + (result.usage.tokensOut ?? 0),
      stream: true,
    })
    return result
  }

  async generate(req: GenerateRequest): Promise<{ text: string; usage: UsageStats }> {
    const response = await fetchOrThrow(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(this.buildBody(req, false)),
      signal: req.signal,
    }, 'Ollama')
    const data = await readJsonOrThrow<{
      message?: { content?: string }
      prompt_eval_count?: number
      eval_count?: number
    }>(response)
    const usage = {
      tokensIn: data.prompt_eval_count ?? null,
      tokensOut: data.eval_count ?? null,
    }
    // Métrica global: tokens IA consumidos (sin contenido sensible).
    void track('ai_request', {
      provider: 'ollama',
      model: this.cfg.model,
      tokens: (usage.tokensIn ?? 0) + (usage.tokensOut ?? 0),
      stream: false,
    })
    return {
      text: data.message?.content ?? '',
      usage,
    }
  }

  async testConnection(): Promise<void> {
    // Verificamos que el servidor responde listando modelos; si no,
    // hacemos una generación mínima.
    try {
      const res = await fetch(`${this.cfg.baseUrl.replace(/\/$/, '')}/api/tags`)
      if (res.ok) return
    } catch {
      // Si /api/tags no está disponible, caemos al test por generación.
    }
    const result = await this.generate({ user: TEST_PROMPT, maxTokens: 5, temperature: 0 })
    if (!result.text) throw new AiError('Ollama devolvió una respuesta vacía.')
  }
}

/**
 * Lee el stream NDJSON de Ollama. Cada línea es un JSON completo;
 * el último objeto tiene `done: true` y las métricas de uso.
 */
async function readOllamaStream(
  response: Response,
  signal: AbortSignal | undefined,
  onToken: StreamHandler,
): Promise<{ text: string; usage: UsageStats }> {
  if (!response.body) throw new AiError('Ollama no devolvió cuerpo.')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let text = ''
  let usage: UsageStats = { tokensIn: null, tokensOut: null }

  try {
    while (true) {
      if (signal?.aborted) {
        reader.cancel().catch(() => {})
        throw new AiError('Generación cancelada.', 499)
      }
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed) continue
        let chunk: {
          message?: { content?: string }
          done?: boolean
          prompt_eval_count?: number
          eval_count?: number
        }
        try {
          chunk = JSON.parse(trimmed)
        } catch {
          continue
        }
        const delta = chunk.message?.content
        if (delta) {
          text += delta
          onToken(delta)
        }
        if (chunk.done) {
          usage = {
            tokensIn: chunk.prompt_eval_count ?? null,
            tokensOut: chunk.eval_count ?? null,
          }
        }
      }
    }
  } finally {
    reader.releaseLock()
  }

  return { text, usage }
}
